/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState, useRef, useEffect } from 'react';
import styles from './pc.module.less';
import { ClassNames } from '@/utils';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import ossImgCompress from '@/utils/ossImgCompress';
import { useGetConfig } from '../../useGetConfig';
import { ProductTracker } from '@/utils/Tracker';

/** 最多展示几张缩略图 */
const SHOW_THUMB_MAX = 8;

const AccessoryCarouselPC = () => {
  const {
    commodityAccessoryList: accessoriesList,
    mobxStore: { app, product },
    skipIndex,
  } = useGetConfig();
  const { webp } = app;
  const { curSelectCommodityId, productDetail, curSelectCommodity } = product;

  /** 当前活动的轮播索引  套餐切换 轮播索引归0 */
  const [activeIndex, setActiveIndex] = useState(0);
  /** 鼠标移入主图 */
  const [isHovered, setIsHovered] = useState(false);
  /** 轮播元素 */
  const splideRef = useRef<Splide | null>(null);
  /** 是否要显示箭头按钮 */
  const showArrow = useMemo(() => {
    return accessoriesList?.length > 1;
  }, [accessoriesList?.length]);

  useEffect(() => {
    /** 切换套餐 索引归0 */
    if (activeIndex !== 0) {
      splideRef.current?.splide?.go(0);
      setActiveIndex(0);
    }
  }, [curSelectCommodityId]);

  // 如果有传轮播索引切换时，则切换到对应索引
  useEffect(() => {
    if (typeof skipIndex === 'number') {
      splideRef.current?.splide?.go(skipIndex);
      setIsHovered(true);
    }
  }, [skipIndex]);

  /** 根据是否超过最大展示数量折叠缩略图列表 */
  const masterList = useMemo(() => {
    const hasFoldingThumbnail = accessoriesList.length > SHOW_THUMB_MAX;
    const sliceThumbList = accessoriesList.slice(0, SHOW_THUMB_MAX - 1);

    return {
      /** 是否展示折叠组件 */
      showMoreDot: hasFoldingThumbnail,
      /** 轮播图 */
      displayList: accessoriesList,
      /** 缩略图 */
      thumbList: hasFoldingThumbnail ? sliceThumbList : accessoriesList,
      /** 折叠缩略图 */
      remainThumbList: hasFoldingThumbnail ? sliceThumbList : [],
    };
  }, [accessoriesList]);

  /** 折叠按钮的文案——当主图数量大于SHOW_THUMB_MAX时才有 */
  const samllMasterText = useMemo(() => {
    const { displayList, thumbList } = masterList;
    if (activeIndex > SHOW_THUMB_MAX - 1) return `+${displayList?.length - activeIndex}`;
    else return `+${displayList?.length - thumbList.length}`;
  }, [activeIndex, masterList.displayList?.length, masterList.thumbList.length]);

  /** 兜里处理 无配件 无场景图 只展示一张合集图 */
  if (masterList.displayList.length < 2) {
    return (
      <div className={styles.onlyImg}>
        <img src={ossImgCompress(masterList.displayList?.[0]?.sceneGraph_url ?? '', { webp })} />
      </div>
    );
  }

  return (
    <div className={styles.carousel} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <div className={styles.container}>
        {showArrow && (
          <div
            className={ClassNames([styles.arrow, styles.arrowL])}
            onClick={() => {
              splideRef.current?.splide?.go('<');
              ProductTracker.productPageImages({
                action: 'click_accessories_left',
                category: 'product_image',
                data: {},
              });
            }}
          >
            <svg
              className={styles.arrSvg}
              width='49'
              height='48'
              viewBox='0 0 49 48'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <g clipPath='url(#clip0_9392_51685)'>
                <path
                  className={styles.arrPath}
                  d='M24.0723 48C37.3271 48 48.0723 37.2548 48.0723 24C48.0723 10.7452 37.3271 0 24.0723 0C10.8174 0 0.0722656 10.7452 0.0722656 24C0.0722656 37.2548 10.8174 48 24.0723 48Z'
                  fill='black'
                  // fillOpacity='0.1'
                />
                <path
                  d='M25.0723 29L20.0723 24L25.0723 19'
                  stroke='white'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  fillOpacity={1}
                />
              </g>
              <defs>
                <clipPath id='clip0_9392_51685'>
                  <rect width='48' height='48' fill='white' transform='translate(0.0722656)' />
                </clipPath>
              </defs>
            </svg>
          </div>
        )}
        {showArrow && (
          <div
            className={ClassNames([styles.arrow, styles.arrowR])}
            onClick={() => {
              splideRef.current?.splide?.go('>');
              ProductTracker.productPageImages({
                action: 'click_accessories_right',
                category: 'product_image',
                data: {},
              });
            }}
          >
            <svg
              className={styles.arrSvg}
              width='49'
              height='48'
              viewBox='0 0 49 48'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <g clipPath='url(#clip0_9392_51688)'>
                <path
                  className={styles.arrPath}
                  d='M24.9277 48C11.6729 48 0.927734 37.2548 0.927734 24C0.927734 10.7452 11.6729 0 24.9277 0C38.1826 0 48.9277 10.7452 48.9277 24C48.9277 37.2548 38.1826 48 24.9277 48Z'
                  fill='black'
                  // fillOpacity='0.3'
                />
                <path
                  d='M23.9277 29L28.9277 24L23.9277 19'
                  stroke='white'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  fillOpacity={1}
                />
              </g>
              <defs>
                <clipPath id='clip0_9392_51688'>
                  <rect width='48' height='48' fill='white' transform='matrix(-1 0 0 1 48.9277 0)' />
                </clipPath>
              </defs>
            </svg>
          </div>
        )}
        <div className={styles.imgContainer}>
          <Splide
            ref={splideRef}
            key={masterList.displayList.length}
            options={{
              pagination: false,
              arrows: false,
              type: 'fade',
              rewind: true,
              lazyLoad: 'nearby',
              preloadPages: 3,
            }}
            className={styles.cusSplide}
            onMove={(splide, newIndex) => {
              setActiveIndex(newIndex);
            }}
          >
            {masterList.displayList.map((item) => {
              return (
                <SplideSlide key={item?.sceneGraph_url}>
                  <img
                    className={styles.master}
                    // onClick={() => { }}
                    src={ossImgCompress(item?.sceneGraph_url ?? '', { webp })}
                    // data-splide-lazy={ossImgCompress(item?.sceneGraph_url ?? "", { webp })}
                    alt=''
                  />
                </SplideSlide>
              );
            })}
          </Splide>
        </div>
        <div className={styles.paginationWrap}>
          <ul data-hover={isHovered}>
            {masterList.thumbList.slice(0, SHOW_THUMB_MAX - 1).map((item, index) => {
              return (
                <li
                  onClick={() => {
                    if (isHovered) {
                      setActiveIndex(index);
                      splideRef.current?.splide?.go(index);
                      ProductTracker.productPageImages({
                        action: 'click_thumbnail_accessories',
                        category: 'product_image',
                        data: {
                          url_key: productDetail?.url_key,
                          name: curSelectCommodity?.name,
                          id: curSelectCommodityId,
                          imageIndex: index + 1,
                        },
                      });
                    }
                  }}
                  onMouseEnter={() => {
                    ProductTracker.productPageImages({
                      action: 'hover_thumbnail_accessories',
                      category: 'product_image',
                      data: {
                        url_key: productDetail?.url_key,
                        name: curSelectCommodity?.name,
                        id: curSelectCommodityId,
                        imageIndex: index + 1,
                      },
                    });
                  }}
                  data-hover={isHovered}
                  key={item.id}
                  className={index === activeIndex ? styles.active : ''}
                >
                  <img className={styles.thumb} src={ossImgCompress(item?.thumbnail_url ?? '', { webp })} alt='' />
                </li>
              );
            })}
            {!isHovered && (
              <div
                className={styles.small}
                data-show={masterList.showMoreDot}
                data-active={activeIndex >= SHOW_THUMB_MAX - 1}
              ></div>
            )}
            {isHovered && masterList.showMoreDot && (
              <div
                onClick={() => {
                  splideRef.current?.splide?.go(SHOW_THUMB_MAX - 1);
                }}
                className={ClassNames([
                  styles.dot,
                  styles.lastDot,
                  activeIndex >= SHOW_THUMB_MAX - 1 ? styles.active : '',
                ])}
              >
                <img
                  className={styles.thumb}
                  src={ossImgCompress(
                    masterList.remainThumbList[activeIndex - SHOW_THUMB_MAX + 1]?.thumbnail_url ??
                      masterList.displayList[SHOW_THUMB_MAX - 1]?.thumbnail_url ??
                      '',
                    { webp }
                  )}
                  alt=''
                />
                <span>{samllMasterText}</span>
              </div>
            )}
            {/* </div>} */}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AccessoryCarouselPC;
